import { api } from '@/lib/api';
import type {
  Shipping,
  ShippingsFilters,
  ShippingsResponse,
  CanadaPostTrackingResult,
} from './types';

export interface AccountOption {
  id: string;
  name: string;
}

/**
 * Get all accounts from V4 API for searchable dropdown filters.
 */
export async function getAllAccountNames(): Promise<AccountOption[]> {
  const PAGE_SIZE = 100;
  const all: AccountOption[] = [];
  let pageNumber = 0;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const response = await api.get<{ accounts: { id: string; name: string }[]; total: number }>(
      '/v4/accounts',
      { pageNumber, pageLength: PAGE_SIZE, sortKey: 'name', sortDirection: 1 },
    );
    const accounts = response.accounts || [];
    for (const a of accounts) {
      all.push({ id: a.id, name: a.name });
    }
    if (accounts.length < PAGE_SIZE) break;
    pageNumber++;
  }

  return all;
}

// =============================================================================
// Shippings API Functions (V4 - PostgreSQL)
// Data source: PostgreSQL via GET /v4/packing-slips
// =============================================================================

/**
 * Get shippings with optional filtering, sorting, and pagination (V4)
 */
export async function getShippings(filters: ShippingsFilters = {}): Promise<ShippingsResponse> {
  const queryParams: Record<string, string | number> = {
    pageNumber: filters.pageNumber ?? 0,
    pageLength: filters.pageLength ?? 25,
    sortKey: filters.sortKey ?? 'createdAt',
    sortDirection: filters.sortDirection ?? -1,
  };

  // Add search filter
  if (filters.search) {
    queryParams.search = filters.search;
  }

  // Add account filter
  if (filters.accountId) {
    queryParams.accountId = filters.accountId;
  }

  // Add status filter
  if (filters.status) {
    queryParams.status = filters.status;
  }

  // Add multiple status filter
  if (filters.status_in && filters.status_in.length > 0) {
    queryParams.status_in = JSON.stringify(filters.status_in);
  }

  // Add shipping company filter
  if (filters.shippingCompany) {
    queryParams.shippingCompany = filters.shippingCompany;
  }

  return api.get<ShippingsResponse>('/v4/packing-slips', queryParams);
}

/**
 * Get a single shipping by ID (V4)
 */
export async function getShipping(id: string): Promise<Shipping> {
  return api.get<Shipping>(`/v4/packing-slips/${id}`);
}

/**
 * Get Canada Post tracking data for multiple tracking numbers
 */
export async function getCanadaPostTracking(
  trackingNumbers: string[]
): Promise<CanadaPostTrackingResult[]> {
  if (trackingNumbers.length === 0) return [];

  const cleanedNumbers = trackingNumbers.map((t) => t.replace(/\s/g, ''));
  return api.get<CanadaPostTrackingResult[]>('/v4/packing-slips/tracking/canada-post', {
    trackingNumbers: cleanedNumbers.join(','),
  });
}

/**
 * Planète Courrier shipment status result
 */
export interface PCShipmentStatus {
  shipmentNumber: string;
  statusCode: string;
  statusName: string;
  statusDate: string | null;
  source: 'cache' | 'api';
}

/**
 * Get Planète Courrier delivery statuses for multiple shipment numbers
 */
export async function getPCShipmentStatuses(
  shipmentNumbers: string[]
): Promise<PCShipmentStatus[]> {
  if (shipmentNumbers.length === 0) return [];
  return api.get<PCShipmentStatus[]>('/v4/planete-courrier/shipments/status', {
    shipmentNumbers: shipmentNumbers.join(','),
  });
}
